import { useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";

// Extracted admin components
import Sidebar from "@/components/admin/Sidebar";
import DashboardOverview from "@/components/admin/DashboardOverview";
import CRMPipeline from "@/components/admin/CRMPipeline";
import ContentManagement from "@/components/admin/ContentManagement";

// Existing page-level components
import CampaignManager from "./CampaignManager";
import TryoutManager from "./TryoutManager";
import SiteContentEditor from "./SiteContentEditor";
import QuickLinksManager from "./QuickLinksManager";
import SponsorsManager from "./SponsorsManager";
import FAQManager from "./FAQManager";
import DripSequences from "./DripSequences";
import NotificationSettings from "./NotificationSettings";
import EmailTemplates from "./EmailTemplates";
import FormBuilder from "./FormBuilder";
import SourceManager from "./SourceManager";
import EmailAnalytics from "./EmailAnalytics";
import SponsorPipeline from "./SponsorPipeline";
import EventsManager from "./EventsManager";
import MapLocationsManager from "./MapLocationsManager";
import ReferralManager from "./ReferralManager";
import HighlightsManager from "./HighlightsManager";
import TeamSnapManager from "./TeamSnapManager";
import ReeplayerInvitesManager from "./ReeplayerInvitesManager";

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Use user from location state if available (passed from AuthCallback)
  const currentUser = location.state?.user || user;

  return (
    <div className="min-h-screen bg-cfc-cream flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-cfc-border p-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </Button>
          <span className="font-heading font-bold text-cfc-primary">CFC Admin</span>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 pb-20 overflow-auto">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="pipeline" element={<CRMPipeline />} />
            <Route path="campaigns" element={<CampaignManager />} />
            <Route path="tryouts" element={<TryoutManager />} />
            <Route path="site-content" element={<SiteContentEditor />} />
            <Route path="pages" element={<SiteContentEditor initialTab="pages" />} />
            <Route path="forms" element={<FormBuilder />} />
            <Route path="sources" element={<SourceManager />} />
            <Route path="site-settings" element={<SiteContentEditor initialTab="settings" />} />
            <Route path="content" element={<ContentManagement />} />
            <Route path="quick-links" element={<QuickLinksManager />} />
            <Route path="sponsors" element={<SponsorsManager />} />
            <Route path="faq" element={<FAQManager />} />
            <Route path="drip-sequences" element={<DripSequences />} />
            <Route path="settings" element={<NotificationSettings />} />
            <Route path="templates" element={<EmailTemplates />} />
            <Route path="email-analytics" element={<EmailAnalytics />} />
            <Route path="sponsor-pipeline" element={<SponsorPipeline />} />
            <Route path="events" element={<EventsManager />} />
            <Route path="map-locations" element={<MapLocationsManager />} />
            <Route path="referrals" element={<ReferralManager />} />
            <Route path="highlights" element={<HighlightsManager />} />
            <Route path="teamsnap" element={<TeamSnapManager />} />
            <Route path="reeplayer-invites" element={<ReeplayerInvitesManager />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
